/**
 * Per-day time and location — SPEC §2.1, §4.4.
 *
 * Two rules pull against each other here, and the resolution matters:
 *
 *  - SPEC §12.2: speed is the feature. The common case is a single-location instructor
 *    following their pattern, and they must never be made to touch a picker.
 *  - SPEC §2.1: an exact time is strongly preferred over AM/PM, and §4.4 means a
 *    multi-location instructor has to be able to say *where*.
 *
 * So these controls live in the summary list rather than in the tap: tapping a day stays
 * one tap and produces a sensible default, and refining it is available underneath for the
 * days that need it. **The location control is hidden entirely** for instructors with only
 * one site, which is most of them.
 */

import type { LocationOption, Occurrence, Period } from '../lib/api.js';

/** Half-hourly through the working day. Covers every start time in the 2026 class report. */
const TIME_OPTIONS = (() => {
  const times: string[] = [];
  for (let hour = 6; hour <= 20; hour++) {
    times.push(`${String(hour).padStart(2, '0')}:00`);
    if (hour < 20) times.push(`${String(hour).padStart(2, '0')}:30`);
  }
  return times;
})();

export interface DayDetailProps {
  occurrence: Occurrence;
  locations: LocationOption[];
  onChange: (next: { startTime?: string; period: Period; locationId: number }) => void;
  disabled?: boolean;
}

export function DayDetail({ occurrence, locations, onChange, disabled }: DayDetailProps) {
  // "ALL_DAY" | "AM" | "PM" | an exact HH:MM
  const value = occurrence.startTime ?? occurrence.period;

  function handleTime(next: string) {
    if (next === 'ALL_DAY' || next === 'AM' || next === 'PM') {
      onChange({ period: next as Period, locationId: occurrence.locationId });
      return;
    }
    // An exact time carries its own period, so AM/PM is derived rather than asked for
    // twice — and the server derives it identically (SPEC §2.1).
    onChange({
      startTime: next,
      period: Number(next.slice(0, 2)) < 12 ? 'AM' : 'PM',
      locationId: occurrence.locationId,
    });
  }

  return (
    <div className="row row--wrap" style={{ gap: 'var(--space-2)', marginTop: 4 }}>
      <label className="visually-hidden" htmlFor={`time-${occurrence.date}`}>
        Time
      </label>
      <select
        id={`time-${occurrence.date}`}
        className="select select--inline"
        value={value}
        disabled={disabled}
        onChange={(e) => handleTime(e.target.value)}
      >
        <option value="ALL_DAY">All day</option>
        <option value="AM">Morning</option>
        <option value="PM">Afternoon</option>
        <optgroup label="Start at">
          {TIME_OPTIONS.map((t) => (
            <option key={t} value={t}>
              {formatTime(t)}
            </option>
          ))}
        </optgroup>
      </select>

      {/* Hidden for single-location instructors — a picker with one option is noise. */}
      {locations.length > 1 && (
        <>
          <label className="visually-hidden" htmlFor={`loc-${occurrence.date}`}>
            Location
          </label>
          <select
            id={`loc-${occurrence.date}`}
            className="select select--inline"
            value={occurrence.locationId}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                ...(occurrence.startTime ? { startTime: occurrence.startTime } : {}),
                period: occurrence.period,
                locationId: Number(e.target.value),
              })
            }
          >
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.canonicalName}
                {l.isHome ? ' (home)' : ''}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

/** `14:30` → `2:30 PM`. Instructors read clock time, not 24-hour. */
export function formatTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const hour = h! % 12 === 0 ? 12 : h! % 12;
  const suffix = h! < 12 ? 'AM' : 'PM';
  return m === 0 ? `${hour} ${suffix}` : `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** How a slot reads in the summary list. */
export function describeSlot(o: Occurrence, locations: LocationOption[]): string {
  const when = o.startTime
    ? formatTime(o.startTime)
    : o.period === 'ALL_DAY'
      ? 'All day'
      : o.period === 'AM'
        ? 'Morning'
        : 'Afternoon';
  const where = locations.find((l) => l.id === o.locationId)?.canonicalName;
  return where && locations.length > 1 ? `${when} · ${where}` : when;
}
